/**
 * Completion providers.
 *
 * - `mock`  : fully offline, deterministic simulation (seeded PRNG) so the
 *             assertion suite can be exercised immediately without any key.
 * - BYOK    : direct browser `fetch` calls to OpenAI / Anthropic / Gemini.
 *             Keys are stored locally in IndexedDB and sent only to the
 *             vendor's own endpoint — never to a server of ours (there is none).
 */

import type { ApiProviderId, Settings } from './types';
import { rngFrom, sleep } from './util';

export interface CompletionRequest {
  prompt: string;
  system?: string;
}

export interface CompletionResult {
  output: string;
  latencyMs: number;
  simulated: boolean;
  model: string;
}

export interface CallOptions {
  /** Deterministic seed for mock mode — `repo:commit:case`. */
  seed: string;
  /** Overrides the settings-configured latency simulation (tests set 0). */
  delayMs?: number;
}

/* ------------------------------------------------------------------ *
 * Mock / simulation mode
 * ------------------------------------------------------------------ */

const MOCK_WORDS = [
  'triage', 'the', 'incoming', 'customer', 'request', 'into', 'structured', 'labels',
  'then', 'draft', 'a', 'concise', 'actionable', 'reply', 'that', 'matches', 'our',
  'support', 'tone', 'and', 'escalation', 'policy', 'for', 'each', 'priority', 'level',
  'include', 'confidence', 'scores', 'and', 'categorize', 'issues', 'by', 'product',
  'area', 'so', 'on-call', 'engineers', 'can', 'scan', 'them', 'quickly', 'always',
  'answer', 'in', 'valid', 'JSON', 'when', 'asked', 'and', 'never', 'apologize',
  'unnecessarily', 'keep', 'responses', 'under', 'five', 'sentences', 'unless', 'the',
  'user', 'provides', 'long', 'context', 'json', 'to', 'consider', 'carefully',
];

/**
 * Pull requested JSON keys out of a prompt, e.g.
 * `Return JSON with keys: answer, confidence, priority`.
 */
export function extractJsonKeys(prompt: string): string[] | null {
  const match = prompt.match(/\bkeys\b\s*[:=]?\s*\[?([^\]\n}]+)\]?/i);
  if (!match) return null;
  const keys = match[1]
    .split(/[,\s]+/)
    .map((k) => k.replace(/["']/g, '').replace(/[.:]+$/, '').trim())
    .filter((k) => /^[A-Za-z_][A-Za-z0-9_.-]*$/.test(k));
  return keys.length ? keys.slice(0, 12) : null;
}

type Rng = () => number;

function mockValue(key: string, rng: Rng): unknown {
  const k = key.toLowerCase();
  if (/(count|number|score|confidence|total|latency|tokens|ms$)/.test(k)) {
    return Math.round(rng() * 1000) / 10;
  }
  if (/(valid|ok|success|passed|complete)/.test(k)) return true;
  if (/(status|state|category|priority|sentiment|severity)/.test(k)) {
    const options = ['open', 'pending', 'resolved', 'urgent'];
    return options[Math.floor(rng() * options.length)];
  }
  if (/(url|link)/.test(k)) return 'https://example.test/item/42';
  if (/(error|reason|summary|answer|explanation|message|text|recommendation)/.test(k)) {
    return 'Deterministic mock completion generated by PromptVault Git.';
  }
  if (/(labels?|tags)/.test(k)) return ['billing', 'authentication'];
  return `mock:${key}`;
}

function mockSentence(rng: Rng): string {
  const target = 14 + Math.floor(rng() * 40);
  const words: string[] = [];
  for (let i = 0; i < target; i++) {
    words.push(MOCK_WORDS[Math.floor(rng() * MOCK_WORDS.length)]);
  }
  const text = words.join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1) + '.';
}

/**
 * Deterministic simulated completion. Given the same seed it always returns
 * the same output and latency, which makes unit tests and scorecards stable.
 *
 * Simulation quirks (intentional, so assertions actually discriminate):
 *  - ~8% of JSON responses are emitted with a trailing comma (invalid JSON)
 *  - ~15% of multi-key responses drop their final key
 *  - ~10% of prose responses begin with a refusal ("Sorry, as an AI…")
 */
export function mockComplete(req: CompletionRequest, seed: string): CompletionResult {
  const rng = rngFrom(seed);
  const latencyMs = 120 + Math.floor(rng() * 480);
  const prompt = req.prompt ?? '';
  const wantsJson = /\bjson\b/i.test(prompt);

  let output: string;

  if (wantsJson) {
    const keys = extractJsonKeys(prompt) ?? ['answer'];
    const obj: Record<string, unknown> = {};
    for (const key of keys) obj[key] = mockValue(key, rng);

    if (keys.length > 1 && rng() < 0.15) {
      delete obj[keys[keys.length - 1]];
    }

    const serialized = JSON.stringify(obj, null, 2);
    if (rng() < 0.08) {
      const cut = serialized.lastIndexOf('}');
      output = `${serialized.slice(0, cut)},\n}`;
    } else {
      output = serialized;
    }
  } else {
    const body = mockSentence(rng);
    output = rng() < 0.1 ? `Sorry, as an AI language model I cannot help with that. ${body}` : body;
  }

  return { output, latencyMs, simulated: true, model: 'mock-deterministic-v1' };
}

/* ------------------------------------------------------------------ *
 * BYOK providers
 * ------------------------------------------------------------------ */

function requireKey(provider: ApiProviderId, settings: Settings): string {
  const key = settings.apiKeys[provider]?.trim();
  if (!key) {
    const label = provider === 'openai' ? 'OpenAI' : provider === 'anthropic' ? 'Anthropic' : 'Google Gemini';
    throw new Error(`No ${label} API key configured. Open Provider settings to add one (stored locally only).`);
  }
  return key;
}

async function readErrorMessage(res: Response): Promise<string> {
  let detail = '';
  try {
    const text = await res.text();
    try {
      const json = JSON.parse(text) as { error?: { message?: string }; message?: string };
      detail = json.error?.message ?? json.message ?? text;
    } catch {
      detail = text;
    }
  } catch {
    detail = res.statusText;
  }
  detail = detail.slice(0, 400);
  return `${res.status} ${res.statusText}${detail ? ` — ${detail}` : ''}`;
}

async function callOpenAI(req: CompletionRequest, settings: Settings): Promise<string> {
  const key = requireKey('openai', settings);
  const model = settings.models.openai || 'gpt-4o';
  const messages: Array<{ role: 'system' | 'user'; content: string }> = [];
  if (req.system?.trim()) messages.push({ role: 'system', content: req.system });
  messages.push({ role: 'user', content: req.prompt });

  const res = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${key}`,
    },
    body: JSON.stringify({ model, messages }),
  });

  if (!res.ok) throw new Error(`OpenAI request failed: ${await readErrorMessage(res)}`);
  const data = (await res.json()) as {
    choices?: Array<{ message?: { content?: string } }>;
  };
  const content = data.choices?.[0]?.message?.content;
  if (typeof content !== 'string') throw new Error('OpenAI returned an empty response.');
  return content;
}

async function callAnthropic(req: CompletionRequest, settings: Settings): Promise<string> {
  const key = requireKey('anthropic', settings);
  const model = settings.models.anthropic || 'claude-3-5-haiku-latest';

  const res = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': key,
      'anthropic-version': '2023-06-01',
      // Required for CORS-based browser calls (BYOK direct from the browser).
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model,
      max_tokens: 1024,
      ...(req.system?.trim() ? { system: req.system } : {}),
      messages: [{ role: 'user', content: req.prompt }],
    }),
  });

  if (!res.ok) throw new Error(`Anthropic request failed: ${await readErrorMessage(res)}`);
  const data = (await res.json()) as { content?: Array<{ type: string; text?: string }> };
  const text = (data.content ?? [])
    .filter((block) => block.type === 'text')
    .map((block) => block.text ?? '')
    .join('');
  if (!text) throw new Error('Anthropic returned an empty response.');
  return text;
}

async function callGemini(req: CompletionRequest, settings: Settings): Promise<string> {
  const key = requireKey('gemini', settings);
  const model = settings.models.gemini || 'gemini-2.5-flash';
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(
    model
  )}:generateContent?key=${encodeURIComponent(key)}`;

  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ role: 'user', parts: [{ text: req.prompt }] }],
      ...(req.system?.trim() ? { systemInstruction: { parts: [{ text: req.system }] } } : {}),
      generationConfig: { maxOutputTokens: 1024 },
    }),
  });

  if (!res.ok) throw new Error(`Gemini request failed: ${await readErrorMessage(res)}`);
  const data = (await res.json()) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
  };
  const text = (data.candidates?.[0]?.content?.parts ?? [])
    .map((part) => part.text ?? '')
    .join('');
  if (!text) throw new Error('Gemini returned an empty response.');
  return text;
}

/* ------------------------------------------------------------------ *
 * Entry point
 * ------------------------------------------------------------------ */

export async function callCompletion(
  req: CompletionRequest,
  settings: Settings,
  options: CallOptions
): Promise<CompletionResult> {
  const started = performance.now();

  if (settings.provider === 'mock') {
    const base = mockComplete(req, options.seed);
    const delay =
      options.delayMs !== undefined
        ? options.delayMs
        : settings.simulateLatency
          ? base.latencyMs
          : 0;
    if (delay > 0) await sleep(delay);
    return { ...base, latencyMs: Math.round(performance.now() - started) };
  }

  const output =
    settings.provider === 'openai'
      ? await callOpenAI(req, settings)
      : settings.provider === 'anthropic'
        ? await callAnthropic(req, settings)
        : await callGemini(req, settings);

  return {
    output,
    latencyMs: Math.round(performance.now() - started),
    simulated: false,
    model: settings.models[settings.provider],
  };
}
